import React from 'react';
import PropTypes from 'prop-types';
import Basepaths from '@mlb/react-patterns/v4/components/Basepaths/basepaths';
import { PlayStatusWrapper, OutsWrapper, BaseRunnerText } from './PlayStatus.style';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';

export const basesResultsShape = {
    first: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    second: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    third: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
};

const outsDictMap = {
    0: {
        key: 'game_noouts',
        text: 'No Outs'
    },
    1: {
        key: 'game_oneout',
        text: 'One Out'
    },
    2: {
        key: 'game_twoouts',
        text: 'Two Outs'
    },
    3: {
        ket: 'game_threeouts',
        text: 'Three Outs'
    }
};

const getOutsText = (balls, strikes, outs, hideCount) => {
    let temp = '';
    const hasBall = typeof balls !== 'undefined';
    const hasStrikes = typeof strikes !== 'undefined';
    const hasOuts = typeof outs !== 'undefined';

    if (hasBall && hasStrikes && !hideCount) {
        temp = `${temp}${balls} - ${strikes}`;
    }

    if (hasOuts) {
        const dicts = outsDictMap[outs];
        temp = `${temp}${temp !== '' ? ', ' : ''}${useTranslator(dicts.key, dicts.text)}`;
    }

    return temp;
};

const getBasesText = (basesResults) => {
    const {
        first,
        second,
        third
    } = basesResults;
    const firstFilled = (typeof first !== 'undefined');
    const secondFilled = (typeof second !== 'undefined');
    const thirdFilled = (typeof third !== 'undefined');
    const emptyBase = !firstFilled && !secondFilled && !thirdFilled;
    const translate = useTranslate();

    // Need to call these all the time, cause hooks and whatever.
    const baseTranslations = {
        empty: translate('basepaths_empty', 'Bases empty'),
        first: translate('basepaths_onFirst', `${first?.person?.lastName} on 1st`, { name: first?.person?.lastName }),
        second: translate('basepaths_onSecond', `${second?.person?.lastName} on 2nd`, { name: second?.person?.lastName }),
        third: translate('basepaths_onThird', `${third?.person?.lastName} on 3rd`, { name: third?.person?.lastName })
    };

    if (emptyBase) {
        return baseTranslations.empty;
    } else {
        const basesNamesArr = [];
        if (firstFilled) {
            basesNamesArr.push(baseTranslations.first);
        }
        if (secondFilled) {
            basesNamesArr.push(baseTranslations.second);
        }
        if (thirdFilled) {
            basesNamesArr.push(baseTranslations.third);
        }
        return basesNamesArr.join(', ');
    }
};

const PlayStatus = ({
    outCount,
    basesResults = {},
    count = {},
    hideCount = false,
    isPlayEvent = false,
    hideOuts = false,
    showBasesText = true
}) => {
    const { balls, strikes, outs } = count;
    let basesResultDiagram;
    if (basesResults) {
        const {
            first,
            second,
            third
        } = basesResults;
        basesResultDiagram = <Basepaths
            first={ first }
            second={ second }
            third={third}
            filledColor={'var(--gameday-basepath-color)'}
            strokeColor={'var(--gameday-basepath-color)'}
            emptyColor='transparent'
            rounded={false}
        />;
    };

    const outText = getOutsText(balls, strikes, outs, hideCount);
    const basesText = getBasesText(basesResults);

    return typeof outCount !== 'undefined' || hideOuts ? (<PlayStatusWrapper>
        { basesResultDiagram }
        { outText && <OutsWrapper isPlayEvent={ isPlayEvent }>{outText}</OutsWrapper>}
        { showBasesText && <BaseRunnerText>{basesText}</BaseRunnerText> }
    </PlayStatusWrapper>) : null;
};

PlayStatus.propTypes = {
    outCount: PropTypes.number,
    basesResults: PropTypes.shape(basesResultsShape),
    count: PropTypes.object,
    hideCount: PropTypes.bool,
    isPlayEvent: PropTypes.bool,
    hideOuts: PropTypes.bool,
    showBasesText: PropTypes.bool
};

export default PlayStatus;
